import { useTranslation } from "@opendash/i18n";
import { Description } from "@opendash/ui";
import { Select } from "antd";
import React from "react";
import { TransformAggregation } from "../types";
import { AggregationSettingInterface } from "./AggregationSettingInterface";

export class TransformAggregationSettings extends AggregationSettingInterface<
  TransformAggregation,
  any
> {
  label: string;

  description: string;
  ExtendedComponent() {
    return (props) => <p>test-extended</p>;
  }
  ViewComponent() {
    return (props) => {
      const { value, onChange, previousValueTypes, inline } = props;
      const initialDim = previousValueTypes.findIndex(
        (vt) => vt.type === "Number"
      );
      const initialVal = value || {
        action: "transform_aggregation",
        params: {
          operation: "min",
          mode: "timeinterval",
          timeinterval: "day",
          dimension: Math.max(0, initialDim),
        } as TransformAggregation["params"],
      };
      const {
        params = {
          operation: "min",
          mode: "timeinterval",
          timeinterval: "day",
          dimension: Math.max(0, initialDim),
        } as TransformAggregation["params"],
      } = initialVal as TransformAggregation;
      const { action } = initialVal;

      if (action !== "transform_aggregation" || !("timeinterval" in params)) {
        onChange({
          action: "transform_aggregation",
          params,
        });
      }
      if (!("timeinterval" in params)) {
        return <pre>Manual Configuration</pre>;
      }
      const [interval, setInterval] = React.useState(params.timeinterval);
      const [aggregationOption, setAggregationOption] = React.useState(
        params.operation
      );

      const [dimension, setDimension] = React.useState(
        params.dimension ?? initialDim
      );
      const t = useTranslation();
      React.useEffect(() => {
        onChange({
          action: "transform_aggregation",
          params: {
            operation: aggregationOption,
            mode: "timeinterval",
            timeinterval: interval,
            dimension,
          },
        });
      }, [interval, aggregationOption, dimension]);

      const options = [
        {
          label: t("opendash:monitoring.history_options.aggregation_none"),
          icon: null,
          tooltip: null,
          value: "none",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_sum"),
          icon: null,
          tooltip: null,
          value: "sum",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_mean"),

          icon: null,
          tooltip: null,
          value: "avg",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_min"),

          icon: null,
          tooltip: null,
          value: "min",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_max"),

          icon: null,
          tooltip: null,
          value: "max",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_first"),

          icon: null,
          tooltip: null,
          value: "first",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_last"),

          icon: null,
          tooltip: null,
          value: "last",
          disabled: false,
        },
        {
          label: t("opendash:monitoring.history_options.aggregation_count"),

          icon: null,
          tooltip: null,
          value: "count",
          disabled: false,
        },
        {
          label: t(
            "opendash:monitoring.history_options.aggregation_diffminmax"
          ),

          icon: null,
          tooltip: null,
          value: "diffminmax",
          disabled: false,
        },
        {
          label: t(
            "opendash:monitoring.history_options.aggregation_diffFirstLast"
          ),

          icon: null,
          tooltip: null,
          value: "diffFirstLast",
          disabled: false,
        },
      ];
      const intervalOptions = [
        {
          label: t("opendash:ui.minute"),
          icon: null,
          tooltip: null,
          value: "minute",
          disabled: false,
        },
        {
          label: t("opendash:ui.hour"),
          icon: null,
          tooltip: null,
          value: "hour",
          disabled: false,
        },
        {
          label: t("opendash:ui.day"),
          icon: null,
          tooltip: null,
          value: "day",
          disabled: false,
        },
        {
          label: t("opendash:ui.week"),
          icon: null,
          tooltip: null,
          value: "week",
          disabled: false,
        },
        {
          label: t("opendash:ui.month"),
          icon: null,
          tooltip: null,
          value: "month",
          disabled: false,
        },
        {
          label: t("opendash:ui.year"),
          icon: null,
          tooltip: null,
          value: "year",
          disabled: false,
        },
        {
          label: t("opendash:ui.interval"),
          icon: null,
          tooltip: null,
          value: "interval",
          disabled: false,
        },
      ];
      return (
        <>
          <div style={{ margin: "10px 0px" }}>
            <Select
              style={{ width: "100%" }}
              options={intervalOptions.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
              value={interval}
              onChange={(selection) => {
                setInterval(selection);
              }}
            />
            {!inline && (
              <Description
                children={t("highcharts:aggregate.multi.interval")}
              ></Description>
            )}
          </div>
          <div style={{ margin: "10px 0px" }}>
            <Select
              style={{ width: "100%" }}
              options={options.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
              value={aggregationOption}
              onChange={(selection) => {
                setAggregationOption(selection);
              }}
            />
            {!inline && (
              <Description
                children={t(
                  "opendash:monitoring.history_options.aggregation_description"
                )}
              ></Description>
            )}
          </div>
          <div style={{ margin: "10px 0px" }}>
            <Select
              style={{ width: "100%" }}
              options={previousValueTypes.map((opt, index) => ({
                label: opt.name,
                value: index,
                disabled: opt.type !== "Number",
              }))}
              value={dimension}
              onChange={(selection) => {
                setDimension(selection);
              }}
            />
            {!inline && (
              <Description
                children={t(
                  "opendash:monitoring.history_options.aggregation_description"
                )}
              ></Description>
            )}
          </div>
        </>
      );
    };
  }
  constructor() {
    super("transform_aggregation");
    this.label = "openware:aggregations.transform_aggregation.label";
    this.description =
      "openware:aggregations.transform_aggregation.description";
  }
  create() {
    return {
      label: this.label,
      description: this.description,
      type: this.type,
      ViewComponent: this.ViewComponent(),
    };
  }
}
